// Chop preview (SPEC F6, v1.1): a 4×4 grid of cues on a record. A cue is a time you tapped; a set pad seeks the player
// there and plays. Seek-based, never audio: nothing is read, rendered or stored from the video.
import { ExternalLink, MoreHorizontal } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppStore } from '../../app/store';
import { padId } from '../../data/kitDefaults';
import type { Cue } from '../../data/types';
import { clearAllCues, clearCue, listCues, nudgeCue, restoreCues, setCue } from '../../db/repos/cues';
import { formatTime } from '../../lib/time';
import { youtubeLink } from '../../lib/youtube';
import { guarded } from '../../lib/writeGuard';
import { BottomSheet } from '../../ui/BottomSheet';
import { Button, IconButton } from '../../ui/Button';
import { Pad } from '../../ui/Pad';
import styles from './ChopGrid.module.css';

/** MPC layout: A01 bottom left, A16 top right (pads are 0-based here). */
const ROWS = [[12, 13, 14, 15], [8, 9, 10, 11], [4, 5, 6, 7], [0, 1, 2, 3]];
const NUDGE_S = 0.1;
const LIT_MS = 180;

export interface ChopGridProps {
  videoId: string;
  durationS: number;
  /** The record is on screen but the player hasn't started it: no time to read yet, but a set pad can start it. */
  armed: boolean;
  /** The player's exact time right now. */
  getTime: () => number;
  /** Seeks and plays. Runs inside the tap. */
  playAt: (atS: number) => void;
}

export function ChopGrid({ videoId, durationS, armed, getTime, playAt }: ChopGridProps) {
  const showToast = useAppStore((s) => s.showToast);
  const [cues, setCues] = useState<Cue[]>([]);
  const [lit, setLit] = useState<number | undefined>();
  const [menu, setMenu] = useState<number | undefined>();
  const litTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const reload = useCallback(async () => {
    setCues(await listCues(videoId).catch(() => []));
  }, [videoId]);
  useEffect(() => {
    let alive = true;
    void listCues(videoId).then((c) => { if (alive) setCues(c); }, () => undefined);
    return () => { alive = false; };
  }, [videoId]);
  useEffect(() => () => clearTimeout(litTimer.current), []);

  const at = (pad: number): number | undefined => cues.find((c) => c.pad === pad)?.atS;
  const flash = (pad: number) => {
    setLit(pad);
    clearTimeout(litTimer.current);
    litTimer.current = setTimeout(() => setLit(undefined), LIT_MS);
  };

  const strike = (pad: number) => {
    flash(pad);
    const cueAt = at(pad);
    if (cueAt !== undefined) {
      playAt(cueAt);
      return;
    }
    if (armed) {
      showToast({ message: 'Play the record first, then tap a pad to set a cue' });
      return;
    }
    const t = getTime();
    void guarded(async () => {
      const cue = await setCue(videoId, pad, t, durationS);
      await reload();
      showToast({
        message: `${padId(pad)} set at ${formatTime(cue.atS, true)}`,
        action: { label: 'Undo', run: () => void guarded(async () => { await clearCue(videoId, pad); await reload(); }) },
      });
    });
  };

  const nudge = (pad: number, delta: number) =>
    void guarded(async () => {
      await nudgeCue(videoId, pad, delta, durationS);
      await reload();
    });

  const setToNow = (pad: number) => {
    if (armed) return;
    const before = at(pad);
    const t = getTime();
    void guarded(async () => {
      const cue = await setCue(videoId, pad, t, durationS);
      await reload();
      showToast({
        message: `${padId(pad)} set to ${formatTime(cue.atS, true)}`,
        action: {
          label: 'Undo',
          run: () => void guarded(async () => {
            if (before === undefined) await clearCue(videoId, pad);
            else await setCue(videoId, pad, before, durationS);
            await reload();
          }),
        },
      });
    });
  };

  const clearOne = (pad: number) => {
    setMenu(undefined);
    void guarded(async () => {
      const gone = await clearCue(videoId, pad);
      await reload();
      if (gone) showToast({ message: `${padId(pad)} cleared`, action: { label: 'Undo', run: () => void guarded(async () => { await restoreCues([gone]); await reload(); }) } });
    });
  };

  const clearAll = () =>
    void guarded(async () => {
      const gone = await clearAllCues(videoId);
      await reload();
      if (gone.length) showToast({ message: `${gone.length} ${gone.length === 1 ? 'cue' : 'cues'} cleared`, action: { label: 'Undo', run: () => void guarded(async () => { await restoreCues(gone); await reload(); }) } });
    });

  const menuAt = menu === undefined ? undefined : at(menu);

  return (
    <section className={styles.chops} aria-label="Chops">
      <div className={styles.head}>
        <h2 className="t-section">Chops</h2>
        <Button variant="tertiary" disabled={cues.length === 0} onClick={clearAll}>Clear all</Button>
      </div>
      <div className={styles.grid} role="group" aria-label="Cue pads A01 to A16">
        {ROWS.flat().map((pad) => {
          const cueAt = at(pad);
          return (
            <div key={pad} className={styles.cell}>
              <Pad
                padId={padId(pad)}
                label={cueAt === undefined ? undefined : formatTime(cueAt, true)}
                lit={lit === pad}
                onStrike={() => strike(pad)}
                onLongPress={cueAt === undefined ? undefined : () => setMenu(pad)}
              />
              {cueAt !== undefined && (
                <IconButton icon={MoreHorizontal} variant="tertiary" aria-label={`Edit ${padId(pad)}`} className={styles.more} onClick={() => setMenu(pad)} />
              )}
            </div>
          );
        })}
      </div>
      <p className={`t-meta ${styles.note}`}>Preview cues, not sample-accurate. Tap an empty pad to set a cue at the playing time.</p>

      <BottomSheet open={menu !== undefined && menuAt !== undefined} title={menu === undefined ? 'Cue' : `${padId(menu)} at ${formatTime(menuAt ?? 0, true)}`} onClose={() => setMenu(undefined)}>
        {menu !== undefined && menuAt !== undefined && (
          <div className={styles.menu}>
            <div className={styles.pair}>
              <Button onClick={() => nudge(menu, -NUDGE_S)}>Earlier 0.1 s</Button>
              <Button onClick={() => nudge(menu, NUDGE_S)}>Later 0.1 s</Button>
            </div>
            <Button disabled={armed} onClick={() => setToNow(menu)}>Set to now</Button>
            <Button onClick={() => clearOne(menu)}>Clear</Button>
            <a className={styles.link} href={youtubeLink(videoId, menuAt)} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={20} strokeWidth={1.75} aria-hidden="true" /> Open on YouTube at this time
            </a>
          </div>
        )}
      </BottomSheet>
    </section>
  );
}
